/**
 * `@tento-lona/sheets/internal-sdk` — the rows-side package's friend entry for
 * the SDK layer (`@tento/lona`, `tento-lona-js/src/`) and nothing else.
 *
 * What the SDK needs from a rows-side sheet it owns and an application never
 * does: `sheetWiring` (the ingestion gate, promote hook, local cell port,
 * local-only attribute / parent mirrors and instrumentation observers), the
 * cell-store change handler types a store mirror subscribes with, the
 * render-skip ledger's key space (`LedgerKeys` / `LedgerBumper`) the SDK's
 * preference clients bump, and the rows-side formula library (`ModelLispLibrary`)
 * the SDK's `Formula` builds on, and the engine names only the SDK's layers
 * read (the sheet model cache, the cell lanes, the alias-template
 * operations, the invalidation coalescer) — off
 * `@tento/lona/sheets/platform`. Each friend name has one door: what the SDK
 * layer reads and the front-ends read too (the live row behind a face, the
 * events index) is on `@tento-lona/sheets/internal-ui` only.
 *
 * A package-private friend entry (shipped, not exported): it ships as a file
 * the published SDK modules import (an exact line in `jsr.json`'s `imports`),
 * and it is NOT in `jsr.json`'s `exports` nor in the root `deno.json`'s import
 * map (it resolves there through the `@tento-lona/sheets/` directory mapping),
 * so a consumer of the package cannot import it; in this repo the boundary
 * test (`platform-barrel-stability`) lets only `tento-lona-js/src/`
 * production code import it. A name here may mirror a testing or
 * internal-testing entry's (the SDK's production code may not import a
 * testing entry), never a published non-testing one — except the names it
 * carries only so a published `src/` entry can re-export them
 * (the repository's barrel-tier check lists each).
 */

// ─── The SDK's door onto a rows-side sheet ───────────────────────
// `sheetOf`: the full `Sheet` behind a rows-side row (whose public face is
// the read-only `row.sheetReads`) — a friend need, not a test need.
export { sheetOf, sheetWiring } from "./sheet/sheet-stores.ts";
// `sheetModelOf`: the frozen forwarding face `SheetHandle.model` hands out;
// `sheetReadsOf`: the frozen reads face the platform doors hand out
// (`platformOf(handle).sheet`, `platformOf(client).openSheet(key)`).
export { sheetModelOf, sheetReadsOf } from "./sheet/rows-sheet-view.ts";
// `cellValueFromLisp`: the one door from a formula's `Lisp.Atom` result into
// a `CellValue` (the SDK's `Formula` stores what its formulas evaluate to).
export { cellValueFromLisp } from "./core/dtype.ts";
export type {
  ListenerDrainObserver,
  RecomputeSweepObserver,
  SheetWideRead,
  SheetWiring,
  SourceInstallEvent,
  SourceInstallObserver,
  StoreInstallObserver,
} from "./sheet/sheet-stores.ts";

// `SurfaceAnchor`: which header / footer surface row a sheet already has
// (server-backed, authored, or a legacy placeholder) — `handle.rows.ensureSurface`
// creates one only when the sheet has no server-backed or authored surface.
export { SurfaceAnchor } from "./render/surface-anchor.ts";

// ─── Local-model row edits (no server verdict) ───────────────────
// Off the public `ModelRow`: label / height / parent / delete of a
// sheet's local copy only.
export { RowLocalEdits } from "./rows/row-local-edits.ts";

// ─── Alias-row writers (`aliasWriterOf`) ─────────────────────────
// Off the public `AliasRow`: its checked prop write, fork and reset. The SDK
// row's alias verbs (`row.alias()?.setProp`) write through it.
export { aliasWriterOf } from "./rows/variants/alias-row.ts";

// ─── Source-row writers (`sourceWriterOf`) ───────────────────────
// Off the public `SourceRow`: its checked / durable / local cell writes.
// The SDK row's verbs write through them.
export { sourceWriterOf } from "./rows/variants/source-row.ts";

// ─── Facade writers (`tasksWriterOf`, `eventsWriterOf`, …) ───────
// Off the public facades (`row.as(TASKS_FACADE)` hands out reads only): the
// cell-level task / event / scheduling-link writes of one row.
export { eventsWriterOf } from "./facades/events-facade.ts";
export { schedulingLinkWriterOf } from "./facades/scheduling-link-facade.ts";
export { tasksWriterOf } from "./facades/tasks-facade.ts";

// ─── Envelope drive (`sdkRow.envelopes.apply`) ───────────────────
// The rows-side half of driving an envelope; the SDK row persists the
// intrinsic edits through its own verbs and mirrors the attribute patch.
export { RowEnvelopeDrive } from "./rows/row-envelopes.ts";

// ─── Cell-store change handlers (store mirrors) ──────────────────
export type {
  CellDataBatchHandler,
  CellDataChange,
  CellDataChangeHandler,
} from "./stores/source-row-cell-data-store.ts";

// ─── Render-skip ledger key space ────────────────────────────────
export { LedgerBumper, LedgerKeys } from "./stores/ledger-keys.ts";

// ─── Cell value → write `data` JSON ──────────────────────────────
// The one encoder a write path sends a cell value through (a canonical
// event's `null` extras left out, as the stored cell has them).
export { cellValueWire } from "./core/dtype.ts";
/** The half-open day / column membership primitive (`ColumnTimepointSearch`). */
export { TimeMembership } from "./render/time-membership.ts";
/** The UTC days an instant range touches, the one rule every cell read names (a core utility both friends carry). */
export { UtcDays } from "./core/utc-days.ts";

// ─── Zone loader ─────────────────────────────────────────────────
// `ZoneLoad`: the one zone loader (a link as its canonical zone, a traced
// fallback, a platform-rebuilt zone traced once). It sits in the rows-side
// core so the events index loads an event's zone through it too.
export { ZoneLoad } from "./core/zone-load.ts";

// ─── Checked indexed read ────────────────────────────────────────
// `elementAt(xs, i, what)`: an index read that throws (naming `what`) on an
// out-of-range slot, where the SDK's layers know the index is in range.
export { elementAt } from "./core/checked-index.ts";

// ─── Rows-side formula library ───────────────────────────────────
export { formulaTestsAbsence, ModelLispLibrary } from "./formula/model-formula.ts";

// ─── Cell-date parse memo ────────────────────────────────────────
// The offline intake's date parses, one per distinct string: the seeded
// backend, the ephemeral spec check, the wire cells and the fetch ingest
// share the rows-side hydrate's memo (`PartialDateMemo`).
export { PartialDateMemo } from "./raw/partial-date-memo.ts";

// ─── Implementor names (off `@tento/lona/sheets/platform`) ───────
// The engine names only the SDK's own layers read: the sheet model cache,
// the cell lanes, the alias-template operations and the builtin task-list
// alias factory, the invalidation coalescer. The reads the SDK layer shares
// with the front-ends (`liveRowOf`, `EventsIndex`, `propsFromNodes`) have one
// door, `@tento-lona/sheets/internal-ui`, which the SDK layer imports too.
export { AliasTemplateOps } from "./aliases/alias-templates.ts";
export { taskListAliasFactory } from "./aliases/builtin-alias-factories.ts";
export { coalesce } from "./tx/invalidation.ts";
// `RowRangeCovered`: the SDK client relays a read's first empty coverage with it.
export { RowRangeCovered } from "./tx/invalidation.ts";
export { SheetModelCache } from "./sheet/sheet-model-cache.ts";
// `RowWrites`: the one row-field ledger (client-scoped, `SheetModelCache.rowWrites`)
// every SDK row write enters.
export { RowWrites } from "./ledger/row-writes.ts";
// `Ledger`: the generic optimistic ledger engine RowWrites and the SDK clients (sheet metadata, tasks, scheduling links) are instances of.
export { Ledger } from "./ledger/ledger.ts";
export { CellLane } from "./stores/cell-lane.ts";
// `MoveWrites`: the one move ledger (a row's placement, a timepoint cell's
// range), and the row facade's writer and intern table — what the SDK's
// `SheetRow.Handle` is built on (`Sheet.facadeFor`).
export { MoveWrites } from "./ledger/move-writes.ts";
export { RowFacade } from "./facades/row-facade.ts";
export { RowWriter } from "./facades/row-writer.ts";

// ─── Generic frozen-collection utility ───────────────────────────
// The SDK layer's own door to the deep freeze it builds its published
// constants with (`sheets/src/core/frozen-collections.ts`): a generic core
// utility, so the SDK takes it here and not through the front-ends' entry.
export { deepFrozen } from "./core/frozen-collections.ts";
